import { Dispatch, SetStateAction } from "react";

import moment from "moment";

import { DATE_FORMATS, TIME_VALUES, useDateTime } from "../../../shared";
import {
  InputValuesDto,
  SCHEDULE_PAYLOAD_NAMES,
  ScheduleDataDto,
  TimeValue
} from "../types";

export const useScheduleDateTimeValues = () => {
  const { formattedDate } = useDateTime();

  const getFormattedTimeInput = (value: string) => {
    if (!value) return null;

    if (value) {
      const [hoursStr, minutesStr] = value.split(":");

      const hours = Number.parseInt(hoursStr) || 0;
      const minutes = Number.parseInt(minutesStr) || 0;

      const normalizedHours = Math.min(hours, 23);

      const normalizedMinutes =
        value.length <= 4
          ? minutes.toString().padStart(2, "0")
          : Math.min(minutes, 59);

      if (value.length <= 5) {
        const formattedTime = moment()
          .set({
            hour: normalizedHours,
            minute: Number(normalizedMinutes)
          })
          .format("HH:mm");

        return formattedTime;
      }
    }
  };

  const hoursValues: TimeValue[] = Array.from(
    { length: 24 },
    (_, hourValue) => ({
      value: hourValue,
      label: hourValue.toString().padStart(2, "0")
    })
  );

  const minutesValues: TimeValue[] = Array.from(
    { length: 12 },
    (_, minuteIndex) => {
      const minuteValue = minuteIndex * 5;

      return {
        value: minuteValue,
        label: minuteValue.toString().padStart(2, "0")
      };
    }
  );

  const formatTimeFromInput = (value: string) => {
    const valueWithNumbersOnly = value.replaceAll(/\D/g, "");

    return valueWithNumbersOnly.length > 2
      ? `${valueWithNumbersOnly.slice(0, 2)}:${valueWithNumbersOnly.slice(2)}`
      : valueWithNumbersOnly;
  };

  const getConvertedTimeValue = (
    value: string,
    datePart: string,
    setStartDateTime: Dispatch<SetStateAction<string | null>>,
    handleFormChange: (
      key: SCHEDULE_PAYLOAD_NAMES,
      value: string | null | boolean
    ) => void
  ) => {
    const formattedValue = formatTimeFromInput(value);

    formattedValue.length <= 5 && setStartDateTime(formattedValue);

    if (
      formattedValue.length === 5 &&
      datePart &&
      formattedValue !== TIME_VALUES.Midnight
    ) {
      const fullDateWithTime = `${datePart} ${formattedValue}`;

      handleFormChange(SCHEDULE_PAYLOAD_NAMES.StartDate, fullDateWithTime);
    }
  };

  const getDateValue = (
    inputValues: InputValuesDto,
    scheduleData: ScheduleDataDto | null | undefined
  ) => {
    if (inputValues[SCHEDULE_PAYLOAD_NAMES.StartDate] !== "") {
      return inputValues[SCHEDULE_PAYLOAD_NAMES.StartDate];
    }

    return scheduleData
      ? formattedDate(
          scheduleData[SCHEDULE_PAYLOAD_NAMES.StartDate],
          DATE_FORMATS.DateHoursMinutes
        )
      : "";
  };

  return {
    hoursValues,
    minutesValues,
    getConvertedTimeValue,
    getDateValue,
    getFormattedTimeInput
  };
};
